import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight, ArrowRight, Briefcase, Sparkles, GraduationCap } from 'lucide-react';
import BackToTopButton from '../components/BackToTopButton';
import { Timeline } from '../components/ui/timeline';
import { BentoGrid } from '../components/ui/bento-grid';


const ABOUT_DATA = {
  hero: {
    label: "ABOUT.ME",
    title: "BEKIRHAN",
    subtitle: "SIMSEK",
    role: "FULL-STACK DEVELOPER",
    location: "ARENDONK, BELGIUM",
    image: "images/proffoto.png",
    description: [
      "My name is Bekirhan Simsek, born in 2004 in Tilburg and currently living in Arendonk. I'm a motivated and eager IT student with a strong ambition to grow within the world of web development and digital technology.",
      "During the corona period, my father asked me to create a website for an acquaintance. Despite having no experience, I decided to take on the challenge. That experience ignited my passion for web development.",
      "Currently, I'm studying Applied Computer Science at Thomas More University of Applied Sciences with a specialization in Application Development."
    ],
  },

  stats: [
    { number: 3, suffix: "", label: "YEARS EXPERIENCE" },
    { number: 7, suffix: "+", label: "PROJECTS COMPLETED" },
    { number: 2026, suffix: "", label: "GRADUATION YEAR" },
  ],

  // Timeline / Journey
  journey: {
    title: "JOURNEY",
    items: [
      {
        year: "2026",
        period: "FEB - MAY",
        title: "Software Developer Intern",
        company: "Matas Electronics BV",
        description: "Building an automated backup monitoring system to replace manual IT checks. Working with Vue.js, PHP, Veeam REST API, SQL Server and Azure. Learning professional development workflows in an EMS/PCB manufacturing environment.",
        type: "work",
      },
      {
        year: "2023",
        period: "2023 - 2026",
        title: "Applied Computer Science Student",
        company: "Thomas More University",
        description: "Specialization in Application Development & AI (APPAI). Graduating end of 2026 with a focus on full-stack development, application architecture, and modern software engineering practices. Specialized branch: Application Developer.",
        type: "education",
      },
      {
        year: "2020",
        period: "",
        title: "First Website",
        company: "WordPress Project",
        description: "During corona I created my first website for an acquaintance. This ignited my passion for web development and digital creation.",
        type: "milestone",
      },
    ],
  },

  // Skills
  skills: {
    title: "SKILLS",
    description: "Technologies and tools I work with to bring ideas to life.",
    categories: [
      { 
        name: "FRONTEND", 
        items: ["JavaScript", "React", "Next.js", "HTML/CSS", "Tailwind CSS","Angular", "Vue.js", "TypeScript","SASS", "Responsive Design", "UI/UX Principles", ],
      },
      { 
        name: "BACKEND", 
        items: ["PHP", "Laravel", ".NET", "Node.js", "Python","Test-Driven Development", "RESTful APIs", "Authentication & Security", "Cloud Deployment (Azure)", "Microservices Architecture"],
      },
      { 
        name: "DATABASE", 
        items: ["MySQL", "MongoDB", "Azure SQL", "SQL Server", "Postman","Database Design", "Query Optimization", "Data Modeling", "ORMs (Eloquent, Sequelize)"],
      },
      { 
        name: "DESIGN", 
        items: ["Figma", "Adobe XD", "UI/UX Design", "Prototyping", "Component Libraries","Atomic Design"],
      },
      { 
        name: "TOOLS", 
        items: ["Git", "GitHub", "Gitlab", "Jira", "VS Code", "Docker","Agile Methodologies", "CI/CD Pipelines", "Unit Testing (PHPUnit)"],
      },
    ],
  },

  // Values
  values: {
    title: "VALUES",
    items: [
      { title: "Clean Code", description: "Writing maintainable, scalable, and efficient code." },
      { title: "User First", description: "Creating experiences that users love and remember." },
      { title: "Never Stop Learning", description: "Constantly evolving with new technologies." },
      { title: "Attention to Detail", description: "Every pixel and interaction matters." },
    ],
  },

  // Ticker texts
  ticker1: ["DEVELOPER", "DESIGNER", "CREATOR", "INNOVATOR"],
  ticker2: ["REACT", "◆", "NODE.JS", "◆", "PYTHON", "◆", "FIGMA", "◆", "AI/ML", "◆"],
};

function AnimatedCounter({ target, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      setCount(0);
      let start = 0;
      const increment = target / (duration * 60);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);

      return () => clearInterval(timer);
    } else {
      setCount(0);
    }
  }, [isInView, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

function AboutPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#0a0a0a] text-white min-h-screen"
    >
      <section className="min-h-screen pt-14">

        {/* Hero Content */}
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-24 items-center">
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Image Container */}
              <div className="relative">
                {/* Profile Image - Smaller size */}
                <div className="relative w-64 h-80 md:w-72 md:h-96 lg:w-80 lg:h-[420px] mx-auto rounded-[32px] overflow-hidden">
                  <img
                    src={ABOUT_DATA.hero.image}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Label Badge */}
                  <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/20">
                    <span className="text-white text-xs tracking-widest font-silkscreen">{ABOUT_DATA.hero.label}</span>
                  </div>
                </div>

                {/* Info Card - Below Image with Gradient Background */}
                <div className="relative -mt-16 mx-4">
                  <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-[24px] p-6 border border-white/10 shadow-2xl">
                    {/* Gradient accent line at top */}
                    <div className="absolute top-0 left-6 right-6 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 rounded-full" />
                    
                    <div className="flex items-center justify-between pt-2">
                      <div>
                        <p className="text-white text-sm tracking-widest font-silkscreen">{ABOUT_DATA.hero.role}</p>
                        <p className="text-white/50 text-xs mt-1 font-silkscreen">{ABOUT_DATA.hero.location}</p>
                      </div>
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/25">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border border-orange-500/30 rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-orange-500/20 rounded-full blur-xl" />
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {/* Title */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight mb-2 font-silkscreen">
                {ABOUT_DATA.hero.title}
              </h1>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-white/30 mb-6 sm:mb-8 font-silkscreen">
                {ABOUT_DATA.hero.subtitle}
              </h2>

              {/* Description */}
              <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-12">
                {ABOUT_DATA.hero.description.map((paragraph, idx) => (
                  <p key={idx} className="text-white/60 text-sm sm:text-base lg:text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Stats - Now with animated counters */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {ABOUT_DATA.stats.map((stat, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + idx * 0.1 }}
                    className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-white/10 text-center group hover:border-orange-500/30 transition-all"
                  >
                    <div className="text-2xl sm:text-3xl lg:text-4xl text-orange-500 mb-1 font-silkscreen">
                      <AnimatedCounter target={stat.number} suffix={stat.suffix} duration={2} />
                    </div>
                    <div className="text-white/40 text-[9px] sm:text-xs tracking-widest font-silkscreen">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-6 sm:mt-8"
              >
                <Link to="/contact">
                  <button className="group flex items-center gap-2 sm:gap-3 bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full transition-all font-silkscreen text-xs sm:text-sm">
                    LET'S WORK TOGETHER
                    <ArrowUpRight className="w-3 sm:w-4 h-3 sm:h-4 group-hover:rotate-45 transition-transform" />
                  </button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Timeline
        data={[{
            title: "2020",
            content: (
              <div>
                <p className="text-white/60 text-sm md:text-base font-normal mb-4">
                  <span className="text-purple-400 font-silkscreen">First Website</span> - <span className="font-semibold">WordPress Project</span>
                </p>
                <p className="text-white/50 text-xs md:text-sm mb-6">
                  During corona I created my first website for an acquaintance. This ignited my passion for web development and digital creation. Despite having no experience, I took on the challenge and discovered my love for coding.
                </p>
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6">
                  <Sparkles className="w-6 h-6 text-purple-400 mb-3" />
                  <p className="text-white/70 text-sm italic">
                    "This project marked the beginning of my journey into web development. It taught me that with determination and curiosity, anything is possible."
                  </p>
                </div>
              </div>
            ),
          },
          
          {
            title: "2023 - 2026",
            content: (
              <div>
                <p className="text-white/60 text-sm md:text-base font-normal mb-4">
                  <span className="text-blue-400 font-silkscreen">Applied Computer Science</span> at <span className="font-semibold">Thomas More University</span>
                </p>
                <p className="text-white/50 text-xs md:text-sm mb-4">
                  Specialization in Application Development & AI (APPAI). Graduating end of 2026 as an Application Developer with expertise in full-stack development, software architecture, and modern development practices.
                </p>
                <p className="text-white/50 text-xs md:text-sm mb-8">
                  Building strong foundations in enterprise application development, cloud technologies, database design, and agile methodologies. Focus on creating scalable, maintainable software solutions.
                </p>
                <div className="mb-4">
                  <div className="flex gap-2 items-center text-white/60 text-xs md:text-sm mb-2">
                    <Briefcase className="w-4 h-4 text-blue-400" />
                    Key Focus Areas
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 font-silkscreen">FULL-STACK DEV</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 font-silkscreen">APP ARCHITECTURE</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 font-silkscreen">AI/ML</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 font-silkscreen">SOFTWARE ENGINEERING</span>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-6">
                  <GraduationCap className="w-6 h-6 text-blue-400 mb-3" />
                  <p className="text-white/70 text-sm">
                    <span className="font-semibold text-blue-400">Graduation: End of 2026</span> - Bachelor of Applied Computer Science, specialized as Application Developer
                  </p>
                </div>
              </div>
            ),
          },{
            title: "2026",
            content: (
              <div>
                <p className="text-white/60 text-sm md:text-base font-normal mb-4">
                  <span className="text-orange-500 font-silkscreen">Software Developer Intern</span> at <span className="font-semibold">Matas Electronics BV</span>
                </p>
                <p className="text-white/50 text-xs md:text-sm mb-4">
                  Building an automated backup monitoring and reporting system for a PCB assembly specialist. The system replaces manual daily IT checks by automatically fetching backup data via the Veeam REST API, processing it, and presenting it through a dashboard with automatic action items on failure.
                </p>
                <p className="text-white/50 text-xs md:text-sm mb-8">
                  Working in an agile environment with sprints, stakeholder demos, and ISO compliance requirements. Learning professional development workflows and collaborating with the IT team in an EMS manufacturing setting.
                </p>
                <div className="mb-4">
                  <div className="flex gap-2 items-center text-white/60 text-xs md:text-sm mb-2">
                    <Briefcase className="w-4 h-4 text-orange-400" />
                    Technologies Used
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-silkscreen">VUE.JS</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-silkscreen">PHP</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-silkscreen">INERTIA.JS</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-silkscreen">VEEAM API</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 font-silkscreen">AZURE DATA STUDIO</span>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-2xl p-6">
                  <Sparkles className="w-6 h-6 text-orange-400 mb-3" />
                  <p className="text-white/70 text-sm italic">
                    "This internship gave me hands-on experience in building enterprise-level automation solutions and working with real-world IT infrastructure challenges."
                  </p>
                </div>
              </div>
            ),
          },
          
        ]}
      />

      <BentoGrid skillsData={ABOUT_DATA.skills} />

      <section className="py-24 lg:py-40 bg-[#0a0a0a] relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 left-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              x: [0, -50, 0],
              y: [0, 50, 0],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-[1800px] mx-auto px-6 lg:px-12 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 lg:mb-24 text-center"
          >
            <span className="text-orange-500 text-xs sm:text-sm tracking-widest mb-4 block font-silkscreen">
              WHAT I BELIEVE
            </span>
            <h2 className="text-4xl lg:text-5xl tracking-tight font-silkscreen mb-4">
              {ABOUT_DATA.values.title}
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Core principles that guide my work and drive me to create exceptional digital experiences.
            </p>
          </motion.div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ABOUT_DATA.values.items.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative"
              >
                {/* Glassmorphic Card */}
                <div className="relative backdrop-blur-xl bg-white/[0.03] rounded-[32px] p-8 border border-white/10 hover:border-orange-500/50 transition-all h-full overflow-hidden shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
                  {/* Glass reflection */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-transparent opacity-50 pointer-events-none" />

                  {/* Glow effect on hover */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Animated Number */}
                    <motion.div
                      className="mb-6"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <span className="text-7xl  bg-gradient-to-br from-orange-500/20 to-orange-500/5 bg-clip-text text-transparent group-hover:from-orange-500/40 group-hover:to-orange-500/10 transition-all font-silkscreen">
                        0{idx + 1}
                      </span>
                    </motion.div>

                    {/* Title with icon effect */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 group-hover:scale-150 transition-transform" />
                      <h3 className="text-xl text-white group-hover:text-orange-500 transition-colors font-silkscreen">
                        {value.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-white/60 group-hover:text-white/80 text-sm leading-relaxed transition-colors">
                      {value.description}
                    </p>

                    {/* Bottom accent line */}
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-[#0a0a0a]">
        <div className="max-w-[99vw] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-black border border-white/10 rounded-[40px] overflow-hidden"
          >
            {/* Subtle gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-white/5" />

            {/* Content */}
            <div className="relative z-10 p-12 lg:p-16 text-center">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-orange-500 text-xs sm:text-sm tracking-widest mb-6 block font-silkscreen"
              >
                LET'S CREATE SOMETHING AMAZING
              </motion.span>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl lg:text-5xl text-white mb-6 font-silkscreen leading-tight"
              >
                Ready to<br />
                <span className="bg-gradient-to-r from-orange-500 to-white bg-clip-text text-transparent">
                  Work Together?
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed"
              >
                I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full transition-all font-silkscreen shadow-lg shadow-orange-500/20"
                  >
                    GET IN TOUCH
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
                <Link to="/projects">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full transition-all border border-white/10 hover:border-white/20 font-silkscreen"
                  >
                    VIEW PROJECTS
                    <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      
      <BackToTopButton />
    </motion.div>
  );
}

export default AboutPage;